import { settingsActions } from 'saur-viseau-state-management/lib/settings/state';
import { isFeatureEnabled } from 'saur-viseau-state-management/lib/settings/selectors';
import Keycloak from 'keycloak-js';
import { store } from './configStore';

// ─── Debug helpers ────────────────────────────────────────────────────────────

/** Retourne true uniquement si le feature flag 'Show-Token-Debug' est activé dans le store Redux */
const isTokenDebugEnabled = (): boolean => {
    try {
        return isFeatureEnabled(store.getState() as any, 'Show-Token-Debug');
    } catch {
        return false;
    }
};

/** Décode le payload d'un JWT sans vérification de signature */
const decodeJwtPayload = (token: string): Record<string, unknown> | null => {
    try {
        const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
        return JSON.parse(atob(base64));
    } catch {
        return null;
    }
};

/**
 * Construit l'URL de logout Keycloak conforme OIDC RP-Initiated Logout (v17+).
 * Utilise `post_logout_redirect_uri` (et non `redirect_uri` qui est l'ancien format).
 *
 * ⚠️ BUG corrigé : `keycloak.createLogoutUrl()` retourne déjà une URL avec `?`.
 * Rajouter `?redirect_uri=...` créait une URL malformée (le second `?` se retrouvait
 * dans la valeur du paramètre `client_id`).
 */
const buildKeycloakLogoutUrl = (
    keycloak: Keycloak | undefined,
    redirectUri: string,
    idToken: string | undefined
): string => {
    const providerConfig = JSON.parse(localStorage.getItem('provider') ?? 'null');
    const url =
        providerConfig?.url ??
        (keycloak as any)?.authServerUrl ??
        (keycloak as any)?.config?.url;
    const realm = providerConfig?.realm ?? (keycloak as any)?.realm;

    // ⚠️ Keycloak est case-sensitive sur le client_id.
    // On lit le `azp` directement depuis le JWT id_token (valeur exacte émise par Keycloak)
    // plutôt que depuis localStorage['provider'] qui peut avoir une casse différente
    // (ex: "CPO-AD-SAUR" dans .env vs "cpo-ad-saur" dans le JWT).
    const clientIdFromJwt = idToken ? decodeJwtPayload(idToken)?.azp as string | undefined : undefined;
    const clientId = clientIdFromJwt ?? providerConfig?.clientId ?? (keycloak as any)?.clientId;

    if (clientIdFromJwt && providerConfig?.clientId && clientIdFromJwt !== providerConfig.clientId) {
        if (isTokenDebugEnabled()) {
            console.warn(
                '[LOGOUT] ⚠️ Mismatch de casse client_id détecté !',
                `JWT azp="${clientIdFromJwt}" vs localStorage="${providerConfig.clientId}"`,
                '→ Utilisation de la valeur JWT (correcte pour Keycloak)'
            );
        }
    }

    if (url && realm && clientId) {
        // Chemin privilégié : construction manuelle propre
        const params = new URLSearchParams({
            post_logout_redirect_uri: redirectUri,
            client_id: clientId,
        });
        if (idToken) params.set('id_token_hint', idToken);
        const finalUrl = `${url}/realms/${realm}/protocol/openid-connect/logout?${params.toString()}`;
        if (isTokenDebugEnabled()) console.log('[LOGOUT] URL construite manuellement :', finalUrl);
        return finalUrl;
    }

    // Fallback : createLogoutUrl() — on s'assure de ne pas doubler le `?`
    if (keycloak && typeof keycloak.createLogoutUrl === 'function') {
        const base = keycloak.createLogoutUrl();
        const separator = base.includes('?') ? '&' : '?';
        const params = new URLSearchParams({ post_logout_redirect_uri: redirectUri });
        if (idToken) params.set('id_token_hint', idToken);
        const finalUrl = `${base}${separator}${params.toString()}`;
        if (isTokenDebugEnabled()) console.warn('[LOGOUT] URL via createLogoutUrl() (fallback) :', finalUrl);
        return finalUrl;
    }

    if (isTokenDebugEnabled()) console.warn('[LOGOUT] Impossible de construire l\'URL Keycloak, redirection simple vers :', redirectUri);
    return redirectUri;
};

// ─────────────────────────────────────────────────────────────────────────────

/**
 * @param source - Origine du logout pour le debug (ex: 'useExpireToken:inactif')
 */
export const handleLogout = async (
    dispatch: any,
    keycloak: Keycloak | undefined,
    redirectUri: string,
    userType: string | null | undefined,
    source: string = 'unknown'
) => {
    try {
        const clearStorage = () => {
            localStorage.clear();
            sessionStorage.clear();
        };

        const logout = (internalUser: boolean, idToken?: string) => {
            // ── LOGS DEBUG (masqués si Show-Token-Debug est désactivé) ──────
            if (isTokenDebugEnabled()) {
                console.group(`%c[LOGOUT] source="${source}" | userType="${userType}" | interne=${internalUser}`, 'color: #e53935; font-weight: bold');

                if (idToken) {
                    const decoded = decodeJwtPayload(idToken);
                    const exp = decoded?.exp as number | undefined;
                    const isExpired = exp ? exp * 1000 < Date.now() : null;
                    console.log('[LOGOUT] id_token (début) :', `${idToken.slice(0, 32)}…`);
                    console.log('[LOGOUT] id_token sub     :', decoded?.sub ?? 'N/A');
                    console.log('[LOGOUT] id_token exp     :', exp ? new Date(exp * 1000).toLocaleTimeString('fr-FR') : 'N/A');
                    console.log(
                        '[LOGOUT] id_token expiré  :',
                        isExpired === null
                            ? 'N/A'
                            : isExpired
                            ? '⚠️ OUI → id_token_hint peut être rejeté si la session Keycloak est déjà invalidée'
                            : '✅ NON'
                    );
                } else {
                    console.warn('[LOGOUT] ⚠️ id_token ABSENT → id_token_hint sera vide, Keycloak ne pourra pas identifier la session');
                }

                console.log('[LOGOUT] Keycloak instance :', keycloak
                    ? `présente | authenticated=${(keycloak as any).authenticated} | realm=${(keycloak as any).realm ?? 'N/A'}`
                    : '⚠️ undefined'
                );
                console.log('[LOGOUT] redirectUri       :', redirectUri);
            }
            // ────────────────────────────────────────────────────────────────

            clearStorage();
            dispatch(settingsActions.logout(internalUser));
            setTimeout(() => {
                dispatch(settingsActions.reset());
                document.cookie =
                    'username=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';

                let logoutUrl = redirectUri;
                if (!internalUser) {
                    logoutUrl = buildKeycloakLogoutUrl(keycloak, redirectUri, idToken);
                }

                if (isTokenDebugEnabled()) {
                    console.log('[LOGOUT] ➡️ window.location.replace :', logoutUrl);
                    console.groupEnd();
                }

                window.location.replace(logoutUrl);
            }, 100);
        };

        if (userType === 'CPO_Saur') {
            logout(true);
        } else {
            if (keycloak) {
                // ⚠️ Lire id_token AVANT clearStorage() qui vide le localStorage
                const idToken = JSON.parse(
                    localStorage.getItem('sso-response') ?? 'null'
                )?.id_token;
                if (isTokenDebugEnabled()) {
                    console.log(
                        '[LOGOUT] Lecture id_token depuis localStorage :',
                        idToken ? `${idToken.slice(0, 20)}…` : '⚠️ null / undefined'
                    );
                }
                logout(false, idToken);
            }
        }
    } catch (error) {
        debugger;
        console.error('[LOGOUT] Erreur lors de la déconnexion :', error);
    }
};

